import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useProjects } from '@/hooks/useProjects';
import { useNoteStyles } from '@/hooks/useNoteStyles';
import { db, transactWithRetry } from '@/lib/db';
import { id } from '@instantdb/react';

const formSchema = z.object({
  title: z.string().min(1, '请输入项目名称').max(50, '项目名称不能超过50个字符'),
  description: z.string().max(200, '描述不能超过200个字符').optional(),
  note_style_id: z.string().min(1, '请选择笔记风格'),
})

interface CreateProjectDialogProps {
  buttonText?: string;
  overrideOrgId?: string;
}

export function CreateProjectDialog({ 
  buttonText = '新建项目',
  overrideOrgId
}: CreateProjectDialogProps) {
  const [open, setOpen] = useState(false);
  const { createProject } = useProjects();
  const { styles, isLoading: styleLoading } = useNoteStyles(overrideOrgId);
  
  const defaultStyleId = styles[0]?.id || '';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      note_style_id: defaultStyleId,
    },
  });

  useEffect(() => {
    if (defaultStyleId) {
      form.setValue('note_style_id', defaultStyleId);
    }
  }, [defaultStyleId, form]);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const selectedStyle = styles.find(s => s.id === values.note_style_id);
      if (!selectedStyle) {
        toast.error('未找到对应的笔记风格');
        return;
      }

      if (overrideOrgId) {
        const projectId = id();
        await transactWithRetry(
          db.tx.projects[projectId].update({
            id: projectId,
            org_id: overrideOrgId,
            title: values.title,
            description: values.description || '',
            note_style_id: values.note_style_id,
            created_at: Date.now(),
          })
        )
      } else {
        await createProject({
          title: values.title,
          description: values.description,
          note_style_id: values.note_style_id,
        });
      }
      
      toast.success('项目创建成功');
      setOpen(false);
      form.reset();
    } catch (error) {
      toast.error('创建失败，请重试');
      console.error(error);
    }
  };

  // 移除自动写入逻辑，初始化已统一在企业层面处理
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{buttonText}</DialogTitle>
          <DialogDescription>
            创建一个新的小红书笔记生成项目，选择适合的风格类型。
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>项目名称</FormLabel>
                  <FormControl>
                    <Input placeholder="例如：春季穿搭分享" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note_style_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>笔记风格</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    value={field.value}
                    disabled={styleLoading}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="选择笔记风格" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {styles.map(style => (
                        <SelectItem key={style.id} value={style.id}>
                          {style.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {styles.length === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      暂无风格，请先写入默认风格
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>项目描述 (可选)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="简要描述项目目标或备注..." 
                      className="resize-none" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {styles.length === 0 && !styleLoading && (
              <div className="p-3 rounded-md border bg-muted/30 text-sm text-destructive font-medium">
                ⚠️ 企业尚未初始化风格数据，请联系管理员。
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={form.formState.isSubmitting || styles.length === 0}>
                {form.formState.isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                创建
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

